import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ReplaySubject, share } from 'rxjs';

export abstract class ClientBaseApi {
  private httpClient = inject(HttpClient);

  private buildUrl(url: string, withCsrf: boolean, params?: Array<string>): string {
    let finalUrl = `${url}`;
    if (params && params.length > 0) {
      finalUrl += `&${params.join('&')}`;
    }
    return finalUrl;
  }

  protected getData<T>(url: string, params?: Array<string>) {
    const finalUrl = this.buildUrl(url, true, params);
    return this.httpClient.get<T>(finalUrl).pipe(
      share({
        connector: () => new ReplaySubject(),
        resetOnRefCountZero: true,
        resetOnComplete: true,
        resetOnError: false,
      }),
    );
  }

  protected postData<T>(url: string, body: any, params: Array<string> = []) {
    const finalUrl = this.buildUrl(url, true, params);
    return this.httpClient.post<T>(finalUrl, body).pipe(
      share({
        connector: () => new ReplaySubject(),
        resetOnRefCountZero: true,
        resetOnComplete: true,
        resetOnError: false,
      }),
    );
  }

  protected putData<T>(url: string, body: any, params?: Array<string>) {
    const finalUrl = this.buildUrl(url, true, params);
    return this.httpClient.put<T>(finalUrl, body).pipe(
      share({
        connector: () => new ReplaySubject(),
        resetOnRefCountZero: true,
        resetOnComplete: true,
        resetOnError: false,
      }),
    );
  }

  protected deleteData<T>(url: string, params?: Array<string>) {
    const finalUrl = this.buildUrl(url, true, params);
    return this.httpClient.delete<T>(finalUrl).pipe(
      share({
        connector: () => new ReplaySubject(),
        resetOnRefCountZero: true,
        resetOnComplete: true,
        resetOnError: false,
      }),
    );
  }

  protected patchData<T>(url: string, body: any, params?: Array<string>) {
    const finalUrl = this.buildUrl(url, true, params);
    return this.httpClient.patch<T>(finalUrl, body).pipe(
      share({
        connector: () => new ReplaySubject(),
        resetOnRefCountZero: true,
        resetOnComplete: true,
        resetOnError: false,
      }),
    );
  }

  protected getDataWithoutSession<T>(url: string, params?: Array<string>) {
    const finalUrl = this.buildUrl(url, false, params);
    return this.httpClient.get<T>(finalUrl, { withCredentials: false }).pipe(
      share({
        connector: () => new ReplaySubject(),
        resetOnRefCountZero: true,
        resetOnComplete: true,
        resetOnError: false,
      }),
    );
  }

  protected deleteDataWithoutSession<T>(url: string, params?: Array<string>) {
    const finalUrl = this.buildUrl(url, false, params);
    return this.httpClient.delete<T>(finalUrl, { withCredentials: false }).pipe(
      share({
        connector: () => new ReplaySubject(),
        resetOnRefCountZero: true,
        resetOnComplete: true,
        resetOnError: false,
      }),
    );
  }

  protected patchDataWithoutSession<T>(url: string, body: any, params?: Array<string>) {
    const finalUrl = this.buildUrl(url, false, params);
    return this.httpClient.patch<T>(finalUrl, body, { withCredentials: false }).pipe(
      share({
        connector: () => new ReplaySubject(),
        resetOnRefCountZero: true,
        resetOnComplete: true,
        resetOnError: false,
      }),
    );
  }

  protected postDataWithoutSession<T>(url: string, body: any, params?: Array<string>) {
    const finalUrl = this.buildUrl(url, false, params);
    return this.httpClient.post<T>(finalUrl, body, { withCredentials: false }).pipe(
      share({
        connector: () => new ReplaySubject(),
        resetOnRefCountZero: true,
        resetOnComplete: true,
        resetOnError: false,
      }),
    );
  }
}
